import { ROLE_CODES, USER_SORT_KEYS, USER_STATUSES, type UserListItem, type UserSortKey, type UserStatus } from "@seagames/contracts";
import { type BulkAction, type ColumnDef, DataTable, Field, IconButton, Icons, type ListChip, ListHeader, ListToolbar, Select, useRowSelection, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ConfirmWithPasswordModal } from "../../components/common/confirm-with-password-modal";
import { usePageBar } from "../../components/shell/page-bar";
import { timestampColumn } from "../../components/common/list-columns";
import { type PasswordReveal, PasswordRevealModal } from "../../components/users/password-reveal-modal";
import { ResetPasswordModal } from "../../components/users/reset-password-modal";
import { FiltersSheet } from "../../components/users/filters-sheet";
import { Avatar, FiltersButton, StatusChip, Tag } from "../../components/users/ui";
import { type UserFormTarget, UserFormModal } from "../../components/users/user-form-modal";
import { requireAdminRole } from "../../lib/auth/guards";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { listSearch } from "../../lib/list-page/list-search";
import { sortSelect } from "../../lib/list-page/sort-select";
import { useListPage } from "../../lib/list-page/use-list-page";
import { userGroupPickerOptions } from "../../lib/user-groups/queries";
import { roleLabel, statusLabel } from "../../lib/users/labels";
import { invalidateUsers, revokeUsersSessions, setUsersStatus, unlockUsers, usersListOptions } from "../../lib/users/queries";
import { toast } from "../../lib/ui/toast";

export const Route = createFileRoute("/_app/users/")({
	// User admin is SUPER_ADMIN / ADMIN only (the API enforces it too).
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: listSearch(USER_SORT_KEYS, { role: z.enum(ROLE_CODES), status: z.enum(USER_STATUSES), user_group_id: z.string().uuid() }),
	component: UsersPage,
});

/** The filters as the URL holds them. `""` = not set; the URL drops empties. */
interface UserFilters extends Record<string, unknown> {
	search: string;
	role: "" | (typeof ROLE_CODES)[number];
	status: "" | UserStatus;
	user_group_id: string;
}
const EMPTY_FILTERS: UserFilters = { search: "", role: "", status: "", user_group_id: "" };
/** The Filters sheet's draft: the fields beyond the search box. */
type SheetDraft = Pick<UserFilters, "role" | "status" | "user_group_id">;

/** A bulk action waiting for the admin's password: re-activate, suspend, or sign out everywhere. */
interface PendingBulk {
	action: "ACTIVE" | "SUSPENDED" | "signOut";
	ids: string[];
}

function UsersPage() {
	const { t } = useTranslation(["users", "common"]);
	const { formatNumber } = useUiStrings();
	const { t: tNav } = useTranslation("nav");
	// The sidebar's own name for this page ("View Users"): the section ("Users") is already the subtitle.
	usePageBar({ title: tNav("items.viewUsers") });
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });

	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => usersListOptions({ ...q, role: search.role, status: search.status, user_group_id: search.user_group_id }),
		columnsKey: "users",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): UserFilters => ({ search: s.search ?? "", role: s.role ?? "", status: s.status ?? "", user_group_id: s.user_group_id ?? "" }),
			toUrl: (f) => ({
				search: f.search.trim() || undefined,
				role: f.role || undefined,
				status: f.status || undefined,
				user_group_id: f.user_group_id || undefined,
			}),
		},
		// Only the summary is per-table ("… of 320 users"); the rest of the chrome comes from AppUiStrings.
		summary: { range: (v) => t("table.summary", v) },
	});
	const selection = useRowSelection();

	// Dialogs. One of each at most; `null` = closed.
	const [form, setForm] = useState<UserFormTarget | null>(null);
	const [resetTarget, setResetTarget] = useState<UserListItem | null>(null);
	const [reveal, setReveal] = useState<PasswordReveal | null>(null);
	const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
	// The Filters sheet: a draft of role / group / status, applied to the URL only on "Filter".
	const [sheet, setSheet] = useState<SheetDraft | null>(null);

	const isSuper = session.active_role === "SUPER_ADMIN";

	const roleOptions = ROLE_CODES.map((r) => ({ value: r, label: roleLabel(t, r) }));
	// The Group filter's options: groups are admin-curated (tens), one capped page by name.
	const groups = useQuery(userGroupPickerOptions());
	const groupOptions = (groups.data?.items ?? []).map((g) => ({ value: g.user_group_id, label: g.name, group: roleLabel(t, g.role_code) }));
	const statusOptions = USER_STATUSES.map((st) => ({ value: st, label: statusLabel(t, st) }));

	const total = page.list.data?.total;
	const sheetCount = [search.role, search.status, search.user_group_id].filter(Boolean).length;
	const openSheet = () => setSheet({ role: search.role ?? "", status: search.status ?? "", user_group_id: search.user_group_id ?? "" });
	const applySheet = () => {
		if (sheet) page.patch({ role: sheet.role || undefined, status: sheet.status || undefined, user_group_id: sheet.user_group_id || undefined });
		setSheet(null);
	};
	const resetSheet = () => {
		page.patch({ role: undefined, status: undefined, user_group_id: undefined });
		setSheet(null);
	};
	const sort = sortSelect<UserSortKey>(
		search,
		"name:asc",
		[
			{ value: "name:asc", label: t("common:sort.nameAsc") },
			{ value: "name:desc", label: t("common:sort.nameDesc") },
			{ value: "created_at:desc", label: t("common:sort.newest") },
			{ value: "created_at:asc", label: t("common:sort.oldest") },
		],
		page.patch,
	);
	// The applied sheet filters as pressed chips — one click takes a filter off.
	const chips: ListChip[] = [
		...(search.role ? [{ kind: "toggle" as const, key: "role", label: roleLabel(t, search.role), pressed: true, onChange: () => page.patch({ role: undefined }) }] : []),
		...(search.user_group_id
			? [
					{
						kind: "toggle" as const,
						key: "group",
						label: groupOptions.find((g) => g.value === search.user_group_id)?.label ?? t("filters.group"),
						pressed: true,
						onChange: () => page.patch({ user_group_id: undefined }),
					},
				]
			: []),
		...(search.status ? [{ kind: "toggle" as const, key: "status", label: statusLabel(t, search.status), pressed: true, onChange: () => page.patch({ status: undefined }) }] : []),
	];

	// A selection that outlives its rows is a trap (bulk-suspend "5 selected" after a filter change).
	// Clear it whenever the filter set changes.
	const filterKey = JSON.stringify([search.search, search.role, search.status, search.user_group_id]);
	useEffect(() => {
		selection.clear();
	}, [filterKey, selection.clear]);

	const runBulk = async (
		label: string,
		fn: (ids: string[]) => Promise<Array<{ item: string; error: unknown }>>,
		ids: string[],
	) => {
		const failures = await fn(ids);
		await invalidateUsers();
		if (failures.length === 0) {
			toast.success(t("bulk.updated", { label, count: ids.length }));
			selection.clear();
		} else {
			toast.error(failures[0]?.error, t("bulk.failedSummary", { label, failed: failures.length, total: ids.length }));
		}
	};

	const bulkActions: BulkAction[] = [
		{
			key: "unlock",
			label: t("bulk.unlock"),
			title: t("bulk.unlockTitle"),
			onClick: (ids) => runBulk(t("bulk.unlock"), unlockUsers, ids),
		},
		// Status changes and sign-outs are SUPER_ADMIN-only on the API, and each asks for the admin's own password first.
		...(isSuper
			? ([
					{
						key: "activate",
						label: t("bulk.activate"),
						variant: "primary",
						onClick: (ids) => setPendingBulk({ action: "ACTIVE", ids }),
					},
					{
						key: "suspend",
						label: t("bulk.suspend"),
						variant: "danger",
						title: t("bulk.suspendTitle"),
						onClick: (ids) => setPendingBulk({ action: "SUSPENDED", ids }),
					},
					{
						key: "sign-out",
						label: t("bulk.signOut"),
						variant: "danger",
						title: t("bulk.signOutTitle"),
						onClick: (ids) => setPendingBulk({ action: "signOut", ids }),
					},
				] satisfies BulkAction[])
			: []),
	];

	/** Runs once the password is typed. A throw keeps the dialog open (it marks a wrong password itself). */
	const confirmBulk = async ({ action, ids }: PendingBulk, password: string) => {
		try {
			if (action === "signOut") {
				const r = await revokeUsersSessions(ids, password);
				toast.success(t("bulk.signedOut", { count: r.user_ids.length }));
			} else {
				const r = await setUsersStatus(ids, action, password);
				const count = r.updated_ids.length;
				toast.success(
					count === 0
						? t("bulk.nothingChanged")
						: action === "SUSPENDED"
							? t("bulk.suspended", { count })
							: t("bulk.activated", { count }),
				);
			}
			selection.clear();
		} finally {
			// Also on failure: with more than 100 selected, earlier batches may already have applied.
			await invalidateUsers();
		}
	};

	const bulkCount = pendingBulk?.ids.length ?? 0;
	const bulkDialog =
		pendingBulk?.action === "signOut"
			? {
					title: t("bulk.confirm.signOutTitle", { count: bulkCount }),
					description: t("bulk.confirm.signOutBody", { count: bulkCount }),
					confirmLabel: t("bulk.signOut"),
					errorPrefix: t("bulk.confirm.signOutFailed"),
					tone: "danger" as const,
				}
			: pendingBulk?.action === "SUSPENDED"
				? {
						title: t("bulk.confirm.suspendTitle", { count: bulkCount }),
						description: t("bulk.confirm.suspendBody", { count: bulkCount }),
						confirmLabel: t("bulk.suspend"),
						errorPrefix: t("bulk.confirm.suspendFailed"),
						tone: "danger" as const,
					}
				: {
						title: t("bulk.confirm.activateTitle", { count: bulkCount }),
						description: t("bulk.confirm.activateBody", { count: bulkCount }),
						confirmLabel: t("bulk.activate"),
						errorPrefix: t("bulk.confirm.activateFailed"),
						tone: "primary" as const,
					};

	const columns: ColumnDef<UserListItem, UserSortKey>[] = [
		{
			key: "name",
			header: t("table.headers.user"),
			sortable: true,
			lockVisible: true,
			minWidth: "13rem",
			cell: (u) => (
				<div className="flex min-w-0 items-center gap-[0.625rem]">
					<Avatar name={u.name} />
					<div className="flex min-w-0 flex-col gap-[0.125rem]">
						<Link
							to="/users/$id"
							params={{ id: u.id }}
							className="truncate text-[0.84375rem] leading-[1.25] font-semibold text-gms-navy-ink hover:text-gms-navy hover:underline"
						>
							{u.name}
						</Link>
						<span className="truncate text-[0.75rem] leading-[1.3] text-gms-ink-3">{u.username}</span>
					</div>
				</div>
			),
		},
		{
			key: "username" as UserSortKey, // sort key; header reads "Contact"
			header: t("table.headers.contact"),
			minWidth: "12rem",
			cell: (u) => (
				<div className="flex min-w-0 flex-col gap-[0.125rem]">
					<span className="truncate text-gms-navy-ink">{u.email ?? <span className="text-gms-ink-3">—</span>}</span>
					{u.phone && <span className="text-[0.75rem] text-gms-ink-3 tabular-nums">{u.phone}</span>}
				</div>
			),
		},
		{
			key: "roles" as UserSortKey,
			header: t("table.headers.roles"),
			minWidth: "10rem",
			cell: (u) => (
				<div className="flex flex-wrap gap-[0.25rem]">
					{u.roles.map((r) => (
						<Tag key={r.code}>{roleLabel(t, r.code)}</Tag>
					))}
				</div>
			),
		},
		{
			key: "user_group" as UserSortKey,
			header: t("table.headers.group"),
			minWidth: "10rem",
			cell: (u) =>
				u.user_group ? (
					<Link
						to="/users/groups/$groupId"
						params={{ groupId: u.user_group.user_group_id }}
						search={{}}
						title={u.user_group.is_active ? roleLabel(t, u.user_group.role_code) : t("table.groupInactiveTitle")}
						className={`truncate hover:underline ${u.user_group.is_active ? "text-gms-navy-ink hover:text-gms-navy" : "text-gms-ink-3"}`}
					>
						{u.user_group.name}
					</Link>
				) : (
					<span className="text-gms-ink-3">—</span>
				),
		},
		{
			key: "status" as UserSortKey,
			header: t("table.headers.status"),
			width: "8rem",
			cell: (u) => (
				<StatusChip status={u.status}>
					{statusLabel(t, u.status)}
					{u.must_change_password && u.status === "ACTIVE" && <span className="sr-only"> {t("table.mustChangePassword")}</span>}
				</StatusChip>
			),
		},
		{
			key: "mfa" as UserSortKey,
			header: t("table.headers.mfa"),
			width: "5rem",
			align: "center",
			cell: (u) =>
				u.mfa_enabled ? <Tag tone="ok">{t("table.mfaOn")}</Tag> : <span className="text-gms-ink-3">—</span>,
		},
		{
			key: "last_login" as UserSortKey,
			header: t("table.headers.lastSignIn"),
			minWidth: "9rem",
			cell: (u) => (
				<span className="whitespace-nowrap text-gms-ink-2 tabular-nums" title={u.last_login_at ? formatDateTime(u.last_login_at) : undefined}>
					{u.last_login_at ? formatShort(u.last_login_at) : <span className="text-gms-ink-3">{t("never")}</span>}
				</span>
			),
		},
		timestampColumn("created_at", t("table.headers.created"), true),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("index.title")}
				count={total === undefined ? undefined : t("index.count", { count: total, n: formatNumber(total) })}
				description={t("index.subtitle")}
				actionLabel={t("index.newUser")}
				onAction={isSuper ? () => setForm({ mode: "create" }) : undefined}
			/>

			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("filters.search")}
				afterSearch={<FiltersButton label={t("filters.button")} count={sheetCount} onClick={openSheet} />}
				sort={sort}
				chips={chips}
				onClearAll={page.clearFilters}
			/>

			<DataTable<UserListItem, UserSortKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(u) => u.id}
				empty={page.emptyState({
					filtered: { title: t("table.empty.noMatchTitle"), description: t("table.empty.noMatchDescription") },
					none: { title: t("table.empty.noneTitle"), description: t("table.empty.noneDescription") },
					clearLabel: t("filters.clear"),
				})}
				selection={{
					selected: selection.selected,
					onChange: selection.onChange,
					// Nobody bulk-suspends themselves by accident.
					isSelectable: (u) => u.id !== session.user.id,
				}}
				bulkActions={bulkActions}
				rowActions={(u) => (
					<div className="flex items-center gap-[0.125rem]">
						<IconButton
							label={t("table.viewAria", { name: u.name })}
							icon={<Icons.Eye />}
							variant="row"
							onClick={() => void navigate({ to: "/users/$id", params: { id: u.id } })}
						/>
						{isSuper && (
							<>
								<IconButton
									label={t("table.editAria", { name: u.name })}
									icon={<Icons.Pencil />}
									variant="row"
									onClick={() => setForm({ mode: "edit", user: u })}
								/>
								{u.id !== session.user.id && (
									<IconButton
										label={t("table.resetPasswordAria", { name: u.name })}
										icon={<Icons.Key />}
										variant="row"
										onClick={() => setResetTarget(u)}
									/>
								)}
							</>
						)}
					</div>
				)}
			/>

			<FiltersSheet open={sheet !== null} onClose={() => setSheet(null)} title={t("filters.sheetTitle")} onApply={applySheet} onReset={resetSheet}>
				<Field label={t("filters.role")} htmlFor="f-role">
					<Select
						id="f-role"
						options={roleOptions}
						value={sheet?.role || null}
						onChange={(v) => setSheet((d) => d && { ...d, role: (v ?? "") as UserFilters["role"] })}
						placeholder={t("filters.allRoles")}
						clearable
					/>
				</Field>
				<Field label={t("filters.group")} htmlFor="f-group">
					<Select
						id="f-group"
						options={groupOptions}
						value={sheet?.user_group_id || null}
						onChange={(v) => setSheet((d) => d && { ...d, user_group_id: v ?? "" })}
						placeholder={t("filters.allGroups")}
						disabled={groups.isPending}
						clearable
					/>
				</Field>
				<Field label={t("filters.status")} htmlFor="f-status">
					<Select
						id="f-status"
						options={statusOptions}
						value={sheet?.status || null}
						onChange={(v) => setSheet((d) => d && { ...d, status: (v ?? "") as UserFilters["status"] })}
						placeholder={t("filters.allStatuses")}
						clearable
					/>
				</Field>
			</FiltersSheet>

			{isSuper && (
				<>
					<UserFormModal
						target={form}
						onClose={() => setForm(null)}
						selfId={session.user.id}
						onReveal={setReveal}
						onResetPassword={(u) => {
							setForm(null);
							setResetTarget(u);
						}}
					/>
					<ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onReveal={setReveal} />
					<PasswordRevealModal reveal={reveal} onClose={() => setReveal(null)} />
					<ConfirmWithPasswordModal
						open={pendingBulk !== null}
						onClose={() => setPendingBulk(null)}
						{...bulkDialog}
						onConfirm={(password) => (pendingBulk ? confirmBulk(pendingBulk, password) : Promise.resolve())}
					/>
				</>
			)}
		</div>
	);
}
